import { useData } from "../../runtime/provider";
import type { DataSession } from "../../data/core/session";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";
import { ReactQueryOptions, trpc } from "@utils/trpc";
import {
  songsByIdQueryKey,
  songsByAlbumIdsQueryKey,
  songsByPlaylistIdsQueryKey,
} from "@utils/queryConfigs";
import type { UseSuspenseQueryOptionsFromConfig } from "@utils/queryConfigs";
import { cachedSongs } from "@sqlite/db";
import {
  songSchema,
  songCreditsWireSchema,
  type Song,
  type SongCredits,
} from "@libs/wire";
import { z } from "zod";
import type { RouterOutputs } from "@utils/trpc";
import chunk from "lodash/chunk";

// Re-export query key helpers for backwards compatibility.
export {
  songsByIdQueryKey,
  songsByAlbumIdsQueryKey,
  songsByPlaylistIdsQueryKey,
};

/**
 * Bidirectional drift check between the wire `Song` type from the catalog
 * router and the locally-defined `songSchema` used by the durable cache.
 * The two must agree (subset + superset) — otherwise valid server data
 * would be rejected at parse time and the cache would pin stale rows.
 *
 * tRPC's `RouterOutputs[...]` is compile-time-only, so this is the
 * runtime-cheap drift detector: one schema, one type, both sides asserted.
 */
type _SongRouterShape = RouterOutputs["catalog"]["songs"]["getById"][number];
type _SongWireSchemaSubset = Song extends _SongRouterShape ? true : never;
type _SongWireSchemaSuperset = _SongRouterShape extends Song ? true : never;
const _songWireSchemaDriftCheck: [
  _SongWireSchemaSubset,
  _SongWireSchemaSuperset,
] = [true, true];
void _songWireSchemaDriftCheck;

const songsListSchema = z.array(songSchema);

const fetchSongsByIds = async (
  data: DataSession,
  songIds: string[],
): Promise<Song[]> => {
  const batches = chunk(songIds, 20);
  const results: Song[] = [];

  for (let i = 0; i < batches.length; i += 5) {
    const batchSlice = batches.slice(i, i + 5);
    const batchPromises = batchSlice.map((batch) =>
      data.api.catalog.songs.getById.query(batch),
    );
    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults.flat());
  }

  return results;
};

export const songsByIdQuery = (data: DataSession, songIds?: string[]) => {
  const ids = songIds ?? [];
  return data.cache.durableEntityQueryOptions<Song>({
    queryKey: songsByIdQueryKey(songIds),
    ids,
    entityTable: cachedSongs,
    idColumn: cachedSongs.songId,
    dataColumn: cachedSongs.data,
    fetch: async (idsToFetch) => fetchSongsByIds(data, idsToFetch),
    schema: songSchema,
    getId: (s) => s.id,
    label: "useSongs",
  });
};

const useSongs = (
  songIds?: string[],
  options?: ReactQueryOptions["catalog"]["songs"]["getById"],
) => {
  const data = useData();
  const query = useQuery({
    ...songsByIdQuery(data, songIds),
    ...options,
    enabled: options?.enabled !== undefined ? options?.enabled : true,
  });

  return { query };
};

type SongCreditsRouterOutput =
  RouterOutputs["catalog"]["songs"]["getCreditsById"];

// Bidirectional drift check pinning songCreditsWireSchema to the router output.
type _SongCreditsSubset =
  z.infer<typeof songCreditsWireSchema> extends SongCreditsRouterOutput
    ? true
    : never;
type _SongCreditsSuperset =
  SongCreditsRouterOutput extends z.infer<typeof songCreditsWireSchema>
    ? true
    : never;
const _songCreditsDriftCheck: [_SongCreditsSubset, _SongCreditsSuperset] = [
  true,
  true,
];
void _songCreditsDriftCheck;

export const songCreditsQueryKey = (songIds?: string[]) =>
  getQueryKey(trpc.catalog.songs.getCreditsById, songIds);

export const songCreditsQuery = (data: DataSession, songIds?: string[]) =>
  data.cache.durableQueryOptions<SongCredits>({
    queryKey: songCreditsQueryKey(songIds),
    queryFn: async () => {
      if (!songIds || songIds.length === 0) {
        return { artists: [] };
      }
      return data.api.catalog.songs.getCreditsById.query(songIds);
    },
    schema: songCreditsWireSchema,
  });

const useSongsCredits = (
  songIds?: string[],
  options?: ReactQueryOptions["catalog"]["songs"]["getCreditsById"],
) => {
  const data = useData();
  const query = useQuery({
    ...songCreditsQuery(data, songIds),
    ...options,
    enabled:
      options?.enabled !== undefined
        ? options?.enabled
        : !!songIds && songIds.length > 0,
  });

  return { query };
};

/**
 * Songs-by-album durable cache. Empty `albumIds` short-circuits to `[]`;
 * queryFn is not invoked.
 *
 * NOTE: the legacy `fetchHandler + downloadManager.getOfflineSongsByAlbumId`
 * substitution path is gone here. The playback queue still uses the offline
 * songs path through `utils/player/data/songs/api.ts` directly. See ADR
 * 0001 + Phase 2.6 cleanup notes.
 */
export const songsByAlbumIdsQuery = (
  data: DataSession,
  albumIds?: string[],
) => {
  const ids = albumIds ?? [];
  const queryKey = songsByAlbumIdsQueryKey(albumIds);
  return data.cache.durableQueryOptions({
    queryKey,
    queryFn: async (): Promise<Song[]> => {
      if (ids.length === 0) return [];
      return data.api.catalog.songs.getByAlbumIds.query(ids);
    },
    schema: songsListSchema,
  });
};

const useSongsByAlbumIds = (
  albumIds: string[] | undefined,
  options?: Omit<
    ReactQueryOptions["catalog"]["songs"]["getByAlbumIds"],
    "suspense"
  >,
) => {
  const data = useData();
  const query = useQuery({
    ...songsByAlbumIdsQuery(data, albumIds),
    ...options,
    enabled: options?.enabled !== undefined ? options?.enabled : true,
  });

  return { query };
};

/**
 * Suspense variant. Cannot use `initialData` (incompatible with suspense
 * semantics — react-query throws if both are present). Cold-start render
 * relies on the queryFn's `withDurableCache` catch-branch to surface a
 * cached row when the network fails.
 */
export const useSongsByAlbumIdsSuspense = (
  albumIds: string[] | undefined,
  options?: Omit<
    ReactQueryOptions["catalog"]["songs"]["getByAlbumIds"],
    "suspense"
  >,
) => {
  const data = useData();
  const config = songsByAlbumIdsQuery(data, albumIds);
  const {
    initialData: _initialData,
    initialDataUpdatedAt: _ts,
    ...rest
  } = config;
  void _initialData;
  void _ts;
  const query = useSuspenseQuery({
    ...rest,
    ...(options as UseSuspenseQueryOptionsFromConfig<typeof config>),
  });

  return { query };
};

/**
 * Songs-by-playlist durable cache. Same shape as `songsByAlbumIdsQuery`.
 */
export const songsByPlaylistIdsQuery = (
  data: DataSession,
  playlistIds: string[],
) => {
  const queryKey = songsByPlaylistIdsQueryKey(playlistIds);
  return data.cache.durableQueryOptions({
    queryKey,
    queryFn: async (): Promise<Song[]> => {
      if (playlistIds.length === 0) return [];
      return data.api.catalog.songs.getByPlaylistIds.query(playlistIds);
    },
    schema: songsListSchema,
  });
};

const useSongsByPlaylistIds = (
  playlistIds: string[],
  options?: Omit<
    ReactQueryOptions["catalog"]["songs"]["getByPlaylistIds"],
    "suspense"
  >,
) => {
  const data = useData();
  const query = useQuery({
    ...songsByPlaylistIdsQuery(data, playlistIds),
    ...options,
    enabled: options?.enabled !== undefined ? options?.enabled : true,
  });

  return { query };
};

// `useSongsByPlaylistIdsSuspense` was removed in Phase 2.6 — zero consumers
// remained after the audit. If a suspense flavour is needed in future,
// mirror `useSongsByAlbumIdsSuspense` (drop initialData; rely on queryFn
// catch-branch).

// `songsByArtistIdsQueryKey` / `songsByArtistIdsQuery` / `useSongsByArtistIds`
// were removed (LIS-305 review task J): zero consumers remained after the
// artist play queue moved to `getTopSongsByArtistId` (top-20, scored) in
// `utils/player/data/collections/api.ts`. That path is online-only and does
// not use this durable-cache hook.

export { useSongs, useSongsCredits, useSongsByAlbumIds, useSongsByPlaylistIds };
